import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Plus, Loader2, Trash2, Building, FileText, Check, X } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

const STATE_COLUMNS = ["bozza", "presentata", "rilasciata", "respinta"];
const STATE_STYLES = {
  bozza: "bg-neutral-100 text-neutral-700 border-neutral-300",
  presentata: "bg-blue-50 text-blue-700 border-blue-300",
  rilasciata: "bg-emerald-50 text-emerald-700 border-emerald-300",
  respinta: "bg-red-50 text-red-700 border-red-300",
};

export default function Pratiche() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, l, c, p] = await Promise.all([
        api.get("/pratiche/meta"),
        api.get("/pratiche"),
        api.get("/clients"),
        api.get("/projects"),
      ]);
      setMeta(m.data);
      setItems(l.data);
      setClients(c.data);
      setProjects(p.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ type: "cila", stato: "bozza", title: "", comune: "", numero_protocollo: "", tecnico: "" });
    setEditOpen(true);
  };
  const openEdit = (it) => { setEditing({ ...it }); setEditOpen(true); };

  const save = async () => {
    if (!editing.title?.trim()) { toast.error("Titolo obbligatorio"); return; }
    try {
      if (editing.id) await api.put(`/pratiche/${editing.id}`, editing);
      else await api.post("/pratiche", editing);
      toast.success("Salvato");
      setEditOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa pratica?")) return;
    await api.delete(`/pratiche/${id}`); toast.success("Eliminata"); await load();
  };

  const changeStato = async (id, stato) => {
    try {
      await api.put(`/pratiche/${id}`, { stato });
      await load();
    } catch (e) { toast.error("Errore cambio stato"); }
  };

  const byStato = useMemo(() => {
    const map = Object.fromEntries(STATE_COLUMNS.map((s) => [s, []]));
    for (const it of items) {
      (map[it.stato] || map.bozza).push(it);
    }
    return map;
  }, [items]);

  if (loading || !meta) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="p-8 lg:p-10 space-y-6 fade-up">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="mono-label flex items-center gap-1.5"><Building className="h-3 w-3" /> Comune / Sportello Edilizia</div>
          <h1 className="font-display text-4xl font-medium text-neutral-900 mt-1">Pratiche Edilizie</h1>
          <p className="text-sm text-neutral-500 mt-2">
            {items.length} pratiche totali · {byStato.presentata.length} in attesa · {byStato.bozza.length} in bozza
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-pratica-btn" className="brand-bg hover:opacity-90">
          <Plus className="h-4 w-4 mr-1.5" /> Nuova pratica
        </Button>
      </header>

      {items.length === 0 ? (
        <div className="border border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500">
          <Building className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
          Nessuna pratica edilizia inserita. Aggiungi CILA, SCIA o Permessi di Costruire per tenere traccia dei protocolli e delle scadenze di integrazione.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {STATE_COLUMNS.map((state) => (
            <div key={state} className="bg-white border border-neutral-200" data-testid={`col-${state}`}>
              <div className="p-3 border-b border-neutral-200 flex items-center justify-between">
                <span className={`inline-flex items-center font-mono text-[10px] font-semibold px-2 py-1 border ${STATE_STYLES[state]}`}>
                  {meta.states.find((s) => s.key === state)?.label || state}
                </span>
                <span className="font-mono text-xs text-neutral-500">{byStato[state].length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[200px]">
                {byStato[state].map((it) => {
                  const typeLabel = meta.types.find((t) => t.key === it.type)?.label || it.type;
                  return (
                    <div key={it.id} className="border border-neutral-200 bg-white p-3 hover:shadow-sm" data-testid={`pratica-${it.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="font-mono text-[10px] font-semibold text-neutral-500">{typeLabel}</span>
                        <button onClick={() => remove(it.id)} className="p-1 text-neutral-400 hover:text-red-600" data-testid={`del-pratica-${it.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      <button onClick={() => openEdit(it)} className="text-left w-full">
                        <div className="font-medium text-sm hover:underline">{it.title}</div>
                        {it.client_name && <div className="text-xs text-neutral-500 mt-0.5">{it.client_name}</div>}
                        {it.comune && <div className="text-xs text-neutral-500">Comune: {it.comune}</div>}
                        {it.numero_protocollo && <div className="font-mono text-[10px] text-neutral-500 mt-1">Prot. {it.numero_protocollo}</div>}
                        {it.scadenza_integrazione && state === "presentata" && (
                          <div className="font-mono text-[10px] text-orange-700 mt-1">Integrazione entro {fmtDate(it.scadenza_integrazione)}</div>
                        )}
                        {it.data_rilascio && state === "rilasciata" && (
                          <div className="font-mono text-[10px] text-emerald-700 mt-1">Rilasciata il {fmtDate(it.data_rilascio)}</div>
                        )}
                      </button>
                      {/* Quick-move buttons */}
                      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-neutral-100">
                        {state === "bozza" && (
                          <button onClick={() => changeStato(it.id, "presentata")} className="font-mono text-[10px] px-2 py-1 border border-blue-200 text-blue-700 hover:bg-blue-50" data-testid={`move-presenta-${it.id}`}>
                            Presenta →
                          </button>
                        )}
                        {state === "presentata" && (
                          <>
                            <button onClick={() => changeStato(it.id, "rilasciata")} className="font-mono text-[10px] px-2 py-1 border border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`move-rilascia-${it.id}`}>
                              <Check className="h-2.5 w-2.5 inline mr-0.5" />Rilascia
                            </button>
                            <button onClick={() => changeStato(it.id, "respinta")} className="font-mono text-[10px] px-2 py-1 border border-red-200 text-red-700 hover:bg-red-50" data-testid={`move-respingi-${it.id}`}>
                              <X className="h-2.5 w-2.5 inline mr-0.5" />Respingi
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                {byStato[state].length === 0 && (
                  <div className="p-4 text-center text-[11px] text-neutral-400 font-mono uppercase">vuoto</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica pratica" : "Nuova pratica edilizia"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Tipo</label>
                  <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} data-testid="pratica-type" className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    {meta.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono-label">Stato</label>
                  <select value={editing.stato} onChange={(e) => setEditing({ ...editing, stato: e.target.value })} data-testid="pratica-stato" className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    {meta.states.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="mono-label">Titolo</label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="pratica-title" placeholder="es. CILA ristrutturazione via Garibaldi 12" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Comune</label>
                  <Input value={editing.comune || ""} onChange={(e) => setEditing({ ...editing, comune: e.target.value })} data-testid="pratica-comune" placeholder="es. Siena" />
                </div>
                <div>
                  <label className="mono-label">N° Protocollo</label>
                  <Input value={editing.numero_protocollo || ""} onChange={(e) => setEditing({ ...editing, numero_protocollo: e.target.value })} data-testid="pratica-prot" placeholder="es. 2025/00123" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Cliente</label>
                  <select value={editing.client_id || ""} onChange={(e) => setEditing({ ...editing, client_id: e.target.value || null })} className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    <option value="">— nessuno —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono-label">Progetto</label>
                  <select value={editing.project_id || ""} onChange={(e) => setEditing({ ...editing, project_id: e.target.value || null })} className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    <option value="">— nessuno —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mono-label">Data presentazione</label>
                  <Input type="date" value={editing.data_presentazione?.slice(0, 10) || ""} onChange={(e) => setEditing({ ...editing, data_presentazione: e.target.value || null })} />
                </div>
                <div>
                  <label className="mono-label">Scad. integrazione</label>
                  <Input type="date" value={editing.scadenza_integrazione?.slice(0, 10) || ""} onChange={(e) => setEditing({ ...editing, scadenza_integrazione: e.target.value || null })} data-testid="pratica-scad-integr" />
                </div>
                <div>
                  <label className="mono-label">Data rilascio</label>
                  <Input type="date" value={editing.data_rilascio?.slice(0, 10) || ""} onChange={(e) => setEditing({ ...editing, data_rilascio: e.target.value || null })} />
                </div>
              </div>
              <div>
                <label className="mono-label">Tecnico incaricato</label>
                <Input value={editing.tecnico || ""} onChange={(e) => setEditing({ ...editing, tecnico: e.target.value })} placeholder="es. Ing. Rossi" />
              </div>
              <div>
                <label className="mono-label">Note</label>
                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="pratica-save" className="brand-bg hover:opacity-90">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
